import ScenePreview, { ScenePreviewSettings } from './ScenePreview';
import { getSize } from 'utils/konva';
import { enableSound } from 'utils/audio/audio';

const castToSeconds = (time: number): number => Math.fround(time / 1000);

export default class VideoScenePreview extends ScenePreview<HTMLVideoElement> {
  public isVideoSeeking = false;

  constructor(settings: ScenePreviewSettings, createVideo = () => document.createElement('video')) {
    super();
    this.element = createVideo();
    this.element.volume = 1;
    this.element.preload = 'auto';
    this.updateSettings(settings);

    this.element.onended = () => {
      this.element.currentTime = this.element.duration;
    };

    this.element.addEventListener('seeked', () => {
      this.isVideoSeeking = false;
    });
  }

  updateVolume(volume: number) {
    this.element.volume = volume;
  }

  updateSpecificSettings() {
    if (this.element.src !== this.settings.src) {
      this.element.src = this.settings.src;
      const { duration, trimStart } = this.settings;
      const currentTime = castToSeconds((this.time ?? 0) + trimStart);
      this.element.currentTime = duration <= this.time ? currentTime : castToSeconds(duration);
    }
    if (!this.settings.isPlaying) this.pause();
  }

  setTime(time: number) {
    const { start, duration, trimStart, isPlaying } = this.settings;
    const end = start + duration - 1;
    this.time = time - start + trimStart;

    this.isVisible = false;

    if (time < start) this.element.currentTime = castToSeconds(trimStart);
    else if (time > end) this.element.currentTime = castToSeconds(trimStart + duration);
    else {
      const isCurrentTimeMatch = Math.abs(this.element.currentTime * 1000 - this.time) < 1000;

      if (!isPlaying || !isCurrentTimeMatch) {
        const currentTime = Math.fround(this.time / 1000);
        if (currentTime || currentTime === 0) {
          this.element.currentTime = currentTime;
        }
      }

      this.isVisible = true;
    }

    this.isVisible && isPlaying ? this.play() : this.pause();
  }

  play() {
    this.element.paused && this.element.play();
  }

  pause() {
    this.element.paused || this.element.pause();
  }

  enableSound() {
    enableSound(this.element);
  }

  drawFrame(ctx: CanvasRenderingContext2D) {
    try {
      const [width, height] = getSize(
        this.settings.scalingMode,
        ctx.canvas.width,
        ctx.canvas.height,
        this.element.videoWidth,
        this.element.videoHeight
      );

      ctx.drawImage(
        this.element,
        (ctx.canvas.width - width) / 2,
        (ctx.canvas.height - height) / 2,
        width,
        height
      );
    } catch (error) {
      // eslint-disable-next-line
    }
  }

  getOriginalWidth() {
    return this.element.videoWidth;
  }

  getOriginalHeight() {
    return this.element.videoHeight;
  }

  getSize(ctx: CanvasRenderingContext2D) {
    return getSize(
      this.settings.scalingMode,
      ctx.canvas.width,
      ctx.canvas.height,
      this.element.videoWidth,
      this.element.videoHeight
    );
  }
}
